import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import {
  ArrowLeftOutlined,
  BarcodeOutlined,
  CheckOutlined,
  DeleteOutlined,
  InboxOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  goodsReceiptsAPI,
  suppliersAPI,
  formatCurrency,
  handleAPIError,
  GR_STATUS
} from '../../services/PurchaseService'
import productService from '../../services/productService'
import { useAuth } from '../../contexts/AuthContext'
import './CreateReceiptPage.css'

const { Title, Text } = Typography
const { TextArea, Search } = Input

const SOURCE_DIRECT = 'direct'
const SOURCE_PO = 'purchase_order'

const asNumber = value => Number(value || 0)

const CreateReceiptPage = () => {
  const { message } = App.useApp()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { id } = useParams()
  const [form] = Form.useForm()
  const [sourceType, setSourceType] = useState(id ? SOURCE_PO : SOURCE_DIRECT)
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [barcodeLoading, setBarcodeLoading] = useState(false)
  const [purchaseOrder, setPurchaseOrder] = useState(null)
  const [pendingOrders, setPendingOrders] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [products, setProducts] = useState([])
  const [receiptItems, setReceiptItems] = useState([])

  const totals = useMemo(() => ({
    ordered: receiptItems.reduce((sum, item) => sum + asNumber(item.quantity_ordered), 0),
    received: receiptItems.reduce((sum, item) => sum + asNumber(item.received_quantity), 0),
    amount: receiptItems.reduce((sum, item) => sum + asNumber(item.received_total), 0)
  }), [receiptItems])

  const loadMasterData = async () => {
    setLoading(true)
    try {
      const [pendingResponse, supplierResponse, productRows] = await Promise.all([
        goodsReceiptsAPI.getPendingOrders(),
        suppliersAPI.getAll(),
        productService.getAllProducts()
      ])
      setPendingOrders(pendingResponse?.data || [])
      setSuppliers(supplierResponse?.data || [])
      setProducts(Array.isArray(productRows) ? productRows : [])
    } catch (error) {
      message.error(handleAPIError(error))
    } finally {
      setLoading(false)
    }
  }

  const fetchPurchaseOrder = async selectedPoId => {
    if (!selectedPoId) return
    setLoading(true)
    try {
      const response = await goodsReceiptsAPI.getPendingOrderById(selectedPoId)
      if (!response.success) return
      const po = response.data
      setPurchaseOrder(po)
      setSourceType(SOURCE_PO)
      setReceiptItems((po.items || []).map(item => ({
        ...item,
        key: `po-${item.poi_id || item.id}`,
        quantity_ordered: asNumber(item.remaining_quantity ?? item.quantity_ordered),
        received_quantity: 0,
        received_total: 0,
        remaining_quantity_ordered: asNumber(item.remaining_quantity ?? item.quantity_ordered),
        product_sku: item.product_sku || item.barcode,
        batch_number: '',
        expiry_date: null,
        location: '',
        serial_numbers_text: ''
      })))
      form.setFieldsValue({
        purchase_id: Number(selectedPoId),
        supplier_id: Number(po.supplier_id),
        supplier_name: po.supplier_name,
        purchase_number: po.purchase_number
      })
    } catch (error) {
      message.error(handleAPIError(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMasterData()
    if (id) fetchPurchaseOrder(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const selectDefaultUnit = (product, preferredUnit) => {
    const units = (product.units || []).filter(unit => unit.is_active !== 0)
    if (preferredUnit?.id) {
      return units.find(unit => Number(unit.id) === Number(preferredUnit.id)) || preferredUnit
    }
    return units.find(unit => Boolean(unit.is_default_purchase))
      || units.find(unit => Boolean(unit.is_base))
      || units[0]
      || null
  }

  const createDirectLine = (product, preferredUnit) => {
    const unit = selectDefaultUnit(product, preferredUnit)
    const factor = asNumber(unit?.conversion_to_base) || 1
    const unitCost = unit?.purchase_price !== null && unit?.purchase_price !== undefined
      ? asNumber(unit.purchase_price)
      : asNumber(product.cost) * factor
    return {
      key: `direct-${product.id}-${unit?.id || 'base'}-${Date.now()}`,
      product_id: Number(product.id),
      product_name: product.name,
      product_sku: product.barcode,
      barcode: product.barcode,
      units: product.units || [],
      product_unit_id: unit?.id || null,
      unit_name_snapshot: unit?.unit_name || product.unit || 'ชิ้น',
      conversion_factor_snapshot: factor,
      unit_cost: unitCost,
      quantity_ordered: null,
      received_quantity: 1,
      received_total: unitCost,
      remaining_quantity_ordered: null,
      track_serial: Boolean(product.track_serial),
      batch_number: '',
      expiry_date: null,
      location: '',
      serial_numbers_text: ''
    }
  }

  const addDirectProduct = (product, preferredUnit) => {
    if (!product?.id) return
    const candidate = createDirectLine(product, preferredUnit)
    setReceiptItems(current => {
      const existingIndex = current.findIndex(item =>
        Number(item.product_id) === Number(candidate.product_id)
        && Number(item.product_unit_id || 0) === Number(candidate.product_unit_id || 0)
      )
      if (existingIndex < 0) return [...current, candidate]
      const next = [...current]
      const quantity = asNumber(next[existingIndex].received_quantity) + 1
      next[existingIndex] = {
        ...next[existingIndex],
        received_quantity: quantity,
        received_total: quantity * asNumber(next[existingIndex].unit_cost)
      }
      return next
    })
    message.success(`เพิ่ม ${product.name} ในรายการรับสินค้าแล้ว`)
  }

  const handleBarcode = async barcode => {
    const value = String(barcode || '').trim()
    if (!value) return
    setBarcodeLoading(true)
    try {
      const product = await productService.getProductByBarcode(value)
      addDirectProduct(product, product.matched_unit)
      form.setFieldValue('barcode', '')
    } catch (error) {
      message.error(error?.response?.status === 404 ? `ไม่พบบาร์โค้ด ${value}` : handleAPIError(error))
    } finally {
      setBarcodeLoading(false)
    }
  }

  const switchSource = value => {
    setSourceType(value)
    setPurchaseOrder(null)
    setReceiptItems([])
    form.setFieldsValue({ purchase_id: undefined, supplier_id: undefined, supplier_name: undefined, purchase_number: undefined })
  }

  const updateReceiptItem = (index, changes) => {
    setReceiptItems(current => {
      const next = [...current]
      const updated = { ...next[index], ...changes }
      updated.received_total = asNumber(updated.received_quantity) * asNumber(updated.unit_cost)
      if (sourceType === SOURCE_PO) {
        updated.remaining_quantity_ordered = Math.max(asNumber(updated.quantity_ordered) - asNumber(updated.received_quantity), 0)
      }
      next[index] = updated
      return next
    })
  }

  const updateQuantity = (index, value) => {
    const quantity = asNumber(value)
    const item = receiptItems[index]
    if (sourceType === SOURCE_PO && quantity > asNumber(item.quantity_ordered)) {
      message.warning(`รับได้สูงสุด ${item.quantity_ordered} ${item.unit_name_snapshot || item.unit || ''}`)
      return
    }
    updateReceiptItem(index, { received_quantity: quantity })
  }

  const updateUnit = (index, unitId) => {
    const item = receiptItems[index]
    const unit = (item.units || []).find(row => Number(row.id) === Number(unitId))
    if (!unit) return
    const factor = asNumber(unit.conversion_to_base) || 1
    const product = products.find(row => Number(row.id) === Number(item.product_id))
    const unitCost = unit.purchase_price !== null && unit.purchase_price !== undefined
      ? asNumber(unit.purchase_price)
      : asNumber(product?.cost) * factor
    updateReceiptItem(index, {
      product_unit_id: unit.id,
      unit_name_snapshot: unit.unit_name,
      conversion_factor_snapshot: factor,
      unit_cost: unitCost
    })
  }

  const receiveAll = () => setReceiptItems(current => current.map(item => ({
    ...item,
    received_quantity: asNumber(item.quantity_ordered),
    remaining_quantity_ordered: 0,
    received_total: asNumber(item.quantity_ordered) * asNumber(item.unit_cost)
  })))

  const validateSerials = status => {
    if (status !== GR_STATUS.CONFIRMED) return true
    const invalid = receiptItems.find(item => {
      if (!item.track_serial || asNumber(item.received_quantity) <= 0) return false
      const serials = String(item.serial_numbers_text || '').split(/[\n,;]+/).map(value => value.trim()).filter(Boolean)
      const required = Math.trunc(asNumber(item.received_quantity) * (asNumber(item.conversion_factor_snapshot) || 1))
      return serials.length !== required
    })
    if (!invalid) return true
    const required = Math.trunc(asNumber(invalid.received_quantity) * (asNumber(invalid.conversion_factor_snapshot) || 1))
    message.warning(`${invalid.product_name} ต้องระบุ Serial ให้ครบ ${required} รายการ`)
    return false
  }

  const handleSave = async (status = GR_STATUS.DRAFT) => {
    try {
      const values = await form.validateFields()
      const receivedItems = receiptItems.filter(item => asNumber(item.received_quantity) > 0)
      if (!receivedItems.length) {
        message.warning('กรุณาเพิ่มสินค้าและระบุจำนวนรับอย่างน้อย 1 รายการ')
        return
      }
      if (!validateSerials(status)) return
      setSaveLoading(true)
      const payload = {
        source_type: sourceType,
        purchase_id: sourceType === SOURCE_PO ? (purchaseOrder?.id || values.purchase_id || id) : null,
        supplier_id: values.supplier_id,
        supplier_document_number: values.supplier_document_number,
        warehouse_name: values.warehouse_name,
        receipt_date: values.receipt_date.format('YYYY-MM-DD'),
        notes: values.notes,
        received_by: user?.id,
        status,
        items: receivedItems.map(item => ({
          ...item,
          expiry_date: item.expiry_date ? dayjs(item.expiry_date).format('YYYY-MM-DD') : null,
          serial_numbers: String(item.serial_numbers_text || '').split(/[\n,;]+/).map(value => value.trim()).filter(Boolean)
        }))
      }
      const response = await goodsReceiptsAPI.create(payload)
      if (response.success) {
        message.success(status === GR_STATUS.CONFIRMED
          ? 'ยืนยันรับสินค้าและเพิ่มสต็อกเข้าคลังแล้ว'
          : 'บันทึกใบรับสินค้าเป็นฉบับร่างแล้ว')
        navigate('/purchases/goods-receipts')
      }
    } catch (error) {
      if (error?.errorFields) return
      message.error(handleAPIError(error))
    } finally {
      setSaveLoading(false)
    }
  }

  const columns = [
    {
      title: 'สินค้า',
      key: 'product',
      width: 250,
      render: (_, record) => (
        <div><Text strong>{record.product_name}</Text><br /><Text type="secondary">{record.product_sku || record.barcode || '-'}</Text></div>
      )
    },
    {
      title: 'หน่วยซื้อ',
      dataIndex: 'unit_name_snapshot',
      width: 150,
      render: (value, record, index) => sourceType === SOURCE_DIRECT && record.units?.length
        ? <Select value={record.product_unit_id || undefined} style={{ width: '100%' }} onChange={unitId => updateUnit(index, unitId)}
            options={record.units.filter(unit => unit.is_active !== 0).map(unit => ({ value: unit.id, label: `${unit.unit_name} (${unit.conversion_to_base} หน่วยฐาน)` }))} />
        : (value || record.unit || 'หน่วยฐาน')
    },
    ...(sourceType === SOURCE_PO ? [{
      title: 'คงเหลือตาม PO', dataIndex: 'quantity_ordered', width: 110, align: 'center'
    }] : []),
    {
      title: 'รับจริง',
      dataIndex: 'received_quantity',
      width: 125,
      render: (value, record, index) => <InputNumber min={0.0001} precision={4} value={value}
        max={sourceType === SOURCE_PO ? asNumber(record.quantity_ordered) : undefined}
        style={{ width: '100%' }} onChange={next => updateQuantity(index, next)} />
    },
    {
      title: 'ต้นทุน/หน่วยซื้อ',
      dataIndex: 'unit_cost',
      width: 145,
      align: 'right',
      render: (value, record, index) => sourceType === SOURCE_DIRECT
        ? <InputNumber min={0} precision={4} value={value} style={{ width: '100%' }} onChange={next => updateReceiptItem(index, { unit_cost: asNumber(next) })} />
        : formatCurrency(value)
    },
    {
      title: 'Batch / Lot', dataIndex: 'batch_number', width: 145,
      render: (value, record, index) => <Input value={value} onChange={event => updateReceiptItem(index, { batch_number: event.target.value })} placeholder="เลข Batch" />
    },
    {
      title: 'วันหมดอายุ', dataIndex: 'expiry_date', width: 145,
      render: (value, record, index) => <DatePicker value={value ? dayjs(value) : null} onChange={date => updateReceiptItem(index, { expiry_date: date })} format="DD/MM/YYYY" style={{ width: '100%' }} />
    },
    {
      title: 'ที่จัดเก็บ', dataIndex: 'location', width: 135,
      render: (value, record, index) => <Input value={value} onChange={event => updateReceiptItem(index, { location: event.target.value })} placeholder="ชั้น/คลัง" />
    },
    {
      title: 'Serial Number', dataIndex: 'serial_numbers_text', width: 220,
      render: (value, record, index) => record.track_serial
        ? <TextArea value={value} autoSize={{ minRows: 1, maxRows: 4 }} onChange={event => updateReceiptItem(index, { serial_numbers_text: event.target.value })} placeholder="หนึ่ง Serial ต่อหนึ่งบรรทัด" />
        : <Text type="secondary">ไม่ติดตาม Serial</Text>
    },
    {
      title: 'มูลค่ารับ', dataIndex: 'received_total', width: 130, align: 'right', render: formatCurrency
    },
    ...(sourceType === SOURCE_DIRECT ? [{
      title: '', key: 'delete', width: 54, fixed: 'right',
      render: (_, record) => <Button danger type="text" icon={<DeleteOutlined />} onClick={() => setReceiptItems(current => current.filter(item => item.key !== record.key))} />
    }] : [])
  ]

  return (
    <div className="create-goods-receipt">
      <Card className="goods-receipt-page-header">
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space align="start">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchases/goods-receipts')}>กลับ</Button>
              <div><Title level={4}><InboxOutlined /> สร้างใบรับสินค้าเข้าคลัง</Title><Text type="secondary">รับตรงด้วยสินค้า/บาร์โค้ด หรือดึงรายการจากใบสั่งซื้อ</Text></div>
            </Space>
          </Col>
          <Col xs={24} md="auto">
            <Space className="goods-receipt-form-actions">
              <Button loading={saveLoading} onClick={() => handleSave(GR_STATUS.DRAFT)}>บันทึกร่าง</Button>
              <Button type="primary" icon={<CheckOutlined />} loading={saveLoading} onClick={() => handleSave(GR_STATUS.CONFIRMED)}>ยืนยันรับเข้าคลัง</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Form form={form} layout="vertical" initialValues={{ receipt_date: dayjs(), warehouse_name: 'คลังหลัก' }}>
        <Card title="แหล่งที่มาของการรับสินค้า" className="goods-receipt-source-card">
          <Segmented block value={sourceType} onChange={switchSource} options={[
            { value: SOURCE_DIRECT, label: 'รับสินค้าโดยตรง', icon: <BarcodeOutlined /> },
            { value: SOURCE_PO, label: 'ดึงจากใบสั่งซื้อ', icon: <ShoppingCartOutlined /> }
          ]} />

          {sourceType === SOURCE_PO ? (
            <Alert type="info" showIcon message="รับตามใบสั่งซื้อ" description="ระบบล็อกรายการและต้นทุนตาม PO แต่รองรับการรับบางส่วนได้" />
          ) : (
            <Alert type="success" showIcon message="รับสินค้าโดยไม่ต้องมีใบสั่งซื้อ" description="เลือกสินค้าเองหรือยิงบาร์โค้ด ระบุหน่วยซื้อ ต้นทุน ล็อต และ Serial ได้โดยตรง" />
          )}
        </Card>

        <Card title="ข้อมูลใบรับสินค้า" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]}>
            {sourceType === SOURCE_PO && (
              <Col xs={24} lg={8}>
                <Form.Item label="ใบสั่งซื้อ" name="purchase_id" rules={[{ required: true, message: 'กรุณาเลือกใบสั่งซื้อ' }]}>
                  <Select showSearch loading={loading} placeholder="ค้นหาเลขที่ PO หรือผู้จำหน่าย" optionFilterProp="label" onChange={fetchPurchaseOrder}
                    options={pendingOrders.map(order => ({ value: order.id, label: `${order.purchase_number} - ${order.supplier_name || '-'}` }))} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12} lg={sourceType === SOURCE_PO ? 8 : 12}>
              <Form.Item label="ผู้จำหน่าย" name="supplier_id" rules={[{ required: true, message: 'กรุณาเลือกผู้จำหน่าย' }]}>
                <Select showSearch disabled={sourceType === SOURCE_PO} optionFilterProp="label" placeholder="เลือกผู้จำหน่าย"
                  options={suppliers.map(supplier => ({ value: supplier.id, label: supplier.company_name || supplier.name || supplier.contact_person || `ผู้จำหน่าย #${supplier.id}` }))} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={sourceType === SOURCE_PO ? 8 : 6}>
              <Form.Item label="วันที่รับสินค้า" name="receipt_date" rules={[{ required: true, message: 'กรุณาเลือกวันที่รับสินค้า' }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="เลขที่เอกสารผู้จำหน่าย" name="supplier_document_number"><Input placeholder="ใบส่งของ / Invoice" /></Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="คลังรับเข้า" name="warehouse_name"><Input placeholder="เช่น คลังหลัก" /></Form.Item>
            </Col>
            {sourceType === SOURCE_PO && (
              <Col xs={24} sm={12} lg={6}><Form.Item label="เลขที่ PO อ้างอิง" name="purchase_number"><Input disabled /></Form.Item></Col>
            )}
            <Col xs={24}><Form.Item label="หมายเหตุ" name="notes"><TextArea rows={2} placeholder="รายละเอียดเพิ่มเติม" /></Form.Item></Col>
          </Row>
        </Card>

        {sourceType === SOURCE_DIRECT && (
          <Card title="เพิ่มสินค้าที่รับเข้า" className="goods-receipt-product-picker">
            <Row gutter={[12, 12]}>
              <Col xs={24} lg={12}>
                <Text strong>เลือกจากรายการสินค้า</Text>
                <Select showSearch loading={loading} placeholder="ค้นหาชื่อสินค้า SKU หรือบาร์โค้ด" optionFilterProp="label" style={{ width: '100%', marginTop: 8 }}
                  value={undefined} onChange={productId => addDirectProduct(products.find(product => Number(product.id) === Number(productId)))}
                  options={products.map(product => ({ value: product.id, label: `${product.name} · ${product.barcode || 'ไม่มีบาร์โค้ด'}` }))} />
              </Col>
              <Col xs={24} lg={12}>
                <Text strong><BarcodeOutlined /> ยิงหรือกรอกบาร์โค้ด</Text>
                <Form.Item name="barcode" noStyle>
                  <Search autoFocus allowClear loading={barcodeLoading} enterButton="รับสินค้า" placeholder="สแกนบาร์โค้ดแล้วกด Enter" onSearch={handleBarcode} style={{ marginTop: 8 }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Card
          title={<Space>รายการรับสินค้า <Tag color="blue">{receiptItems.length} รายการ</Tag></Space>}
          extra={<Space>{sourceType === SOURCE_PO && <Button onClick={receiveAll}>รับทั้งหมด</Button>}<Button onClick={() => setReceiptItems([])}>ล้างรายการ</Button></Space>}
          style={{ marginBottom: 16 }}
        >
          <Table rowKey="key" loading={loading} dataSource={receiptItems} columns={columns} pagination={false} size="small" scroll={{ x: 1450 }}
            locale={{ emptyText: sourceType === SOURCE_DIRECT ? 'เลือกสินค้าหรือยิงบาร์โค้ดเพื่อเริ่มรับสินค้า' : 'เลือกใบสั่งซื้อเพื่อดึงรายการสินค้า' }} />
        </Card>

        <Card title="สรุปการรับสินค้า">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={12} md={6}><Text type="secondary">จำนวนรายการ</Text><Title level={4}>{receiptItems.length}</Title></Col>
            {sourceType === SOURCE_PO && <Col xs={12} md={6}><Text type="secondary">จำนวนตาม PO</Text><Title level={4}>{totals.ordered}</Title></Col>}
            <Col xs={12} md={6}><Text type="secondary">จำนวนหน่วยซื้อที่รับ</Text><Title level={4} style={{ color: '#389e0d' }}>{totals.received}</Title></Col>
            <Col xs={24} md={sourceType === SOURCE_PO ? 6 : 12} className="goods-receipt-total-value">
              <Text type="secondary">มูลค่ารับเข้ารวม</Text><Title level={3}>{formatCurrency(totals.amount)}</Title>
            </Col>
          </Row>
          <Divider />
          <Text type="secondary">สินค้าและสต็อกจะเพิ่มจริงเมื่อกด “ยืนยันรับเข้าคลัง” เท่านั้น การบันทึกร่างยังไม่เปลี่ยนยอดสต็อก</Text>
        </Card>
      </Form>
    </div>
  )
}

export default CreateReceiptPage
